import {
  useState,
  useEffect,
} from 'react';
import {
  Gavel,
  FileText,
  Calendar,
  MapPin,
  DollarSign,
  TrendingDown,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  type SourcingEvent,
  SOURCING_STATUS_LABELS,
  SOURCING_STATUS_COLORS,
  EVENT_TYPE_LABELS,
  EVENT_TYPE_COLORS,
} from '@/features/sourcing/types';

interface SourcingEventCardProps {
  readonly event: SourcingEvent;
  readonly onViewDetails?: (event: SourcingEvent) => void;
}

export function SourcingEventCard({
  event, onViewDetails 
}: SourcingEventCardProps) {
  const [timeRemaining, setTimeRemaining] = useState('');

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: event.currency,
    }).format(amount);

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });

  const formatTime = (dateStr: string) =>
    new Date(dateStr).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });

  const isReverseAuction = event.eventType === 'reverse_auction';

  useEffect(() => {
    if (!isReverseAuction) return;

    const updateTimer = () => {
      const now = Date.now();
      const endTime = event.auctionEndTime ? new Date(event.auctionEndTime).getTime() : 0;
      const startTime = event.auctionStartTime ? new Date(event.auctionStartTime).getTime() : 0;

      if (now < startTime) {
        const diff = startTime - now;
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        setTimeRemaining(`Starts in ${hours}h ${mins}m`);
      } else if (now < endTime) {
        const diff = endTime - now;
        const mins = Math.floor(diff / (1000 * 60));
        const secs = Math.floor((diff % (1000 * 60)) / 1000);
        setTimeRemaining(`${mins}:${secs.toString().padStart(2, '0')}`);
      } else {
        setTimeRemaining('Ended');
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [isReverseAuction, event.auctionStartTime, event.auctionEndTime]);

  const now = Date.now();
  const auctionStart = event.auctionStartTime ? new Date(event.auctionStartTime).getTime() : 0;
  const auctionEnd = event.auctionEndTime ? new Date(event.auctionEndTime).getTime() : 0;
  const isLive = isReverseAuction && now >= auctionStart && now < auctionEnd;

  const getAuctionStatusLabel = () => {
    if (isLive) return 'Live';
    if (event.status === 'scheduled') return 'Scheduled';
    return 'Auction';
  };

  return (
    <Card className={`hover:shadow-md transition-shadow ${isLive ? 'ring-2 ring-orange-500' : ''}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${isReverseAuction ? 'bg-orange-100' : 'bg-indigo-100'}`}>
              {isReverseAuction ? (
                <Gavel className="h-5 w-5 text-orange-600" />
              ) : (
                <FileText className="h-5 w-5 text-indigo-600" />
              )}
            </div>
            <div>
              <CardTitle className="text-base">{event.title}</CardTitle>
              <p className="text-sm text-muted-foreground">{event.eventNumber}</p>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${EVENT_TYPE_COLORS[event.eventType]}`}>
              {EVENT_TYPE_LABELS[event.eventType]}
            </span>
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${SOURCING_STATUS_COLORS[event.status]}`}>
              {SOURCING_STATUS_LABELS[event.status]}
            </span>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {isReverseAuction && (
          <div className={`rounded-lg p-3 ${isLive ? 'bg-orange-50 border border-orange-200' : 'bg-muted/50'}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium flex items-center gap-1">
                <TrendingDown className="h-4 w-4" />
                {getAuctionStatusLabel()}
              </span>
              {timeRemaining && (
                <span className={`text-sm font-mono ${isLive ? 'text-orange-600 font-bold' : ''}`}>
                  {timeRemaining}
                </span>
              )}
            </div>
            {event.currentLowestBid === undefined ? (
              <div>
                <p className="text-xs text-muted-foreground">Starting Price</p>
                <p className="font-semibold">{formatCurrency(event.startingPrice ?? 0)}</p>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Current Lowest</p>
                  <p className="text-lg font-bold text-green-600">{formatCurrency(event.currentLowestBid)}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-muted-foreground">Bids</p>
                  <p className="font-semibold">{event.totalBids ?? 0}</p>
                </div>
              </div>
            )}
          </div>
        )}

        {isReverseAuction === false && (
          <div className="rounded-lg p-3 bg-indigo-50 border border-indigo-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Deadline</p>
                <p className="font-semibold">{formatDate(event.bidDeadline)} {formatTime(event.bidDeadline)}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-muted-foreground">Proposals</p>
                <p className="font-semibold">{event.totalBids ?? 0}</p>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-2 text-sm">
          <div className="flex items-center gap-1">
            <MapPin className="h-3 w-3 text-muted-foreground" />
            <span className="truncate">{event.deliveryZone}</span>
          </div>
          <div className="flex items-center gap-1">
            <Calendar className="h-3 w-3 text-muted-foreground" />
            <span>{formatDate(event.requiredDeliveryDate)}</span>
          </div>
          <div className="flex items-center gap-1 col-span-2">
            <DollarSign className="h-3 w-3 text-muted-foreground" />
            <span>Est. {formatCurrency(event.estimatedValue)}</span>
          </div>
        </div>

        <div className="flex gap-2 pt-2">
          <Button variant="outline" size="sm" className="flex-1" onClick={() => onViewDetails?.(event)}>
            View Details
          </Button>
          {event.status === 'evaluation' && (
            <Button size="sm" className="flex-1">
              Review Bids
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
